import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    TemplateRef,
    ChangeDetectionStrategy,
} from '@angular/core';
import {
    Evaluation,
    EvaluationMatrix,
    EvaluationStandard,
    EvaluationStandardRubricLevel,
    KpiResultPeriod,
} from '@masar/common/models';
import { ConfirmService } from '@masar/features/confirm-dialog';
import { EvaluateService } from '@masar/features/evaluate/services/evaluate.service';
import {
    EvaluationInstance,
    EvaluationStandardRecord,
    EvaluationStandardRecordValue,
    EvaluationType,
} from '@masar/features/evaluate/types';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';
import { NotificationService } from 'mnm-webapp';

@Component({
    selector: 'app-rubric-evaluation-instance-detail',
    templateUrl: './rubric-evaluation-instance-detail.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class RubricEvaluationInstanceDetailComponent implements OnInit {
    @Input() public entityId?: string;

    @Input() public savedEntityId?: string;

    @Input() public initialEvaluationId?: string;

    @Input() public itemId?: string;

    @Input() public type!: EvaluationType;

    @Input() public mode: 'default' | 'view' = 'default';

    @Input() public customAction?: {
        name: string;
        template: TemplateRef<any>;
        item: KpiResultPeriod;
    };

    @Input() public hideNotes = false;

    @Output() public update = new EventEmitter<EvaluationInstance>();

    public item?: Partial<EvaluationInstance>;

    public evaluations: Evaluation[] = [];

    public selectedEvaluation: Evaluation | null = null;

    public groupedMatrices: EvaluationMatrix[] = [];

    public standaloneStandards: EvaluationStandard[] = [];

    public hasNoEvaluationDefinition = false;

    public isSubmitting = false;

    public isLoadingDefinition = false;

    public hasEmptyValue = false;

    public hasValueOutOfRange = false;

    public readonly guidePreviewLimit = 120;

    private standards: EvaluationStandard[] = [];

    private readonly recordsByStandardId = new Map<
        string,
        EvaluationStandardRecord
    >();

    public constructor(
        private readonly evaluateService: EvaluateService,
        private readonly confirmService: ConfirmService,
        private readonly translateService: TranslateService,
        private readonly notificationService: NotificationService
    ) {}

    public ngOnInit(): void {
        if (this.itemId) {
            this.getItem();
            return;
        }

        this.evaluateService
            .listEvaluations(this.type, this.savedEntityId)
            .subscribe(items => {
                this.evaluations = items.filter(
                    x => (x.inputType ?? 'standard') === 'rubric'
                );

                if (this.evaluations.length > 0) {
                    this.setSelectedEvaluation(
                        this.evaluations.find(
                            x => x.id === this.initialEvaluationId
                        ) ??
                            this.evaluations.find(x => x.isDefault) ??
                            this.evaluations[0]
                    );
                    return;
                }

                this.hasNoEvaluationDefinition = true;
                this.createItem();
            });
    }

    public trackByIndex(index: number): number {
        return index;
    }

    public setSelectedEvaluation(evaluation: Evaluation): void {
        this.selectedEvaluation = evaluation ?? null;
        this.hasNoEvaluationDefinition = false;

        if (!this.selectedEvaluation?.id) {
            this.groupedMatrices = [];
            this.standaloneStandards = [];
            this.standards = [];
            this.createItem();
            return;
        }

        this.loadDefinition(this.selectedEvaluation.id);
    }

    public getRecordForStandard(
        standard: EvaluationStandard
    ): EvaluationStandardRecord | null {
        const standardId = standard?.id;
        if (standardId && this.recordsByStandardId.has(standardId)) {
            return this.recordsByStandardId.get(standardId) ?? null;
        }

        return (
            (this.item?.records ?? []).find(
                x =>
                    x.standardId === standardId ||
                    (x.name ?? '') === (standard.name ?? '')
            ) ?? null
        );
    }

    public getSelectedLevel(
        record: EvaluationStandardRecord,
        standard: EvaluationStandard
    ): EvaluationStandardRubricLevel | null {
        if (
            !record?.selectedRubricLevelId &&
            !record?.selectedRubricLevelName
        ) {
            return null;
        }

        return (
            standard.rubricLevels?.find(
                x =>
                    x.rubricLevelId === record.selectedRubricLevelId ||
                    x.rubricLevelName === record.selectedRubricLevelName
            ) ?? null
        );
    }

    public selectLevel(
        record: EvaluationStandardRecord,
        standard: EvaluationStandard,
        level: EvaluationStandardRubricLevel
    ): void {
        record.selectedRubricLevelId = level.rubricLevelId;
        record.selectedRubricLevelName = level.rubricLevelName;
        record.selectedRubricLevelColor = level.color;

        if (record.value === undefined || record.value === null) {
            record.value = this.getDefaultLevelValue(level);
        }

        if (this.isValueOutOfRange(record, standard)) {
            record.value = this.getDefaultLevelValue(level);
        }

        this.checkForEmptyValues();
    }

    public isSelectedLevel(
        record: EvaluationStandardRecord,
        level: EvaluationStandardRubricLevel
    ): boolean {
        return record.selectedRubricLevelId === level.rubricLevelId;
    }

    public isValueOutOfRange(
        record: EvaluationStandardRecord,
        standard: EvaluationStandard
    ): boolean {
        if (record?.value === undefined || record?.value === null) {
            return false;
        }

        const level = this.getSelectedLevel(record, standard);

        if (!level) {
            return false;
        }

        if (
            level.from !== undefined &&
            level.from !== null &&
            record.value < level.from
        ) {
            return true;
        }

        if (
            level.to !== undefined &&
            level.to !== null &&
            record.value > level.to
        ) {
            return true;
        }

        return false;
    }

    public checkForEmptyValues(): void {
        const records = this.item?.records ?? [];

        this.hasEmptyValue = records.some(
            x =>
                !x.selectedRubricLevelId ||
                x.value === undefined ||
                x.value === null
        );

        this.hasValueOutOfRange = this.getFlattenedStandards(
            this.groupedMatrices,
            this.standaloneStandards
        ).some(standard => {
            const record = this.getRecordForStandard(standard);

            return record ? this.isValueOutOfRange(record, standard) : false;
        });
    }

    public processUpdate(): void {
        const records = this.item?.records ?? [];
        const note = this.item?.note ?? '';

        if (
            this.isSubmitting ||
            !records.length ||
            this.hasEmptyValue ||
            this.hasValueOutOfRange
        ) {
            return;
        }

        this.isSubmitting = true;

        let observable;

        if (this.itemId) {
            observable = this.evaluateService.updateInstance(
                this.itemId,
                this.type,
                records.map(record => this.mapRecordPayload(record, false)),
                note
            );
        } else {
            const entityId = this.entityId;
            const evaluationId = this.selectedEvaluation?.id;

            if (!entityId || !evaluationId) {
                this.isSubmitting = false;
                return;
            }

            observable = this.evaluateService.createInstance(
                entityId,
                this.type,
                evaluationId,
                records.map(record => this.mapRecordPayload(record, true)),
                note
            );
        }

        observable
            .pipe(finalize(() => (this.isSubmitting = false)))
            .subscribe((item: EvaluationInstance) => {
                const successMessage = this.itemId
                    ? 'translate_item_updated_successfully'
                    : 'translate_item_added_successfully';

                this.notificationService.notifySuccess(
                    this.translateService.instant(successMessage)
                );

                this.item = item;
                this.refreshRecordsMap();
                this.update.emit(item);
            });
    }

    public async showGuideDetails(guide?: string): Promise<void> {
        const text = (guide ?? '').trim();

        if (!text) {
            return;
        }

        await this.confirmService.alert({
            title: this.translateService.instant('translate_guide'),
            text,
            confirmButtonText: this.translateService.instant('translate_ok'),
            size: { width: '45%' },
        });
    }

    public getCriteriaCount(matrix: EvaluationMatrix): number {
        return matrix.standards?.length ?? 0;
    }

    private getItem(): void {
        if (!this.itemId) {
            return;
        }

        this.evaluateService
            .getInstance(this.itemId, this.type)
            .subscribe(item => {
                this.item = item;
                this.refreshRecordsMap();

                const evaluationId = item.evaluationId;

                if (evaluationId) {
                    this.loadExistingDefinitionById(evaluationId);
                    return;
                }

                this.evaluateService
                    .listEvaluations(
                        this.type,
                        this.savedEntityId ?? this.entityId
                    )
                    .subscribe(items => {
                        this.evaluations = items.filter(
                            x => (x.inputType ?? 'standard') === 'rubric'
                        );

                        const selectedEvaluation =
                            this.evaluations.find(x => x.name === item.name) ??
                            this.evaluations[0] ??
                            null;

                        if (!selectedEvaluation?.id) {
                            this.hasNoEvaluationDefinition = true;
                            this.checkForEmptyValues();
                            return;
                        }

                        this.loadExistingDefinitionById(selectedEvaluation.id);
                    });
            });
    }

    private loadExistingDefinitionById(evaluationId: string): void {
        this.loadDefinition(evaluationId, true);
    }

    private loadDefinition(
        evaluationId: string,
        keepExistingRecords = false
    ): void {
        this.isLoadingDefinition = true;

        this.evaluateService.getEvaluation(evaluationId).subscribe({
            next: evaluation => {
                this.selectedEvaluation = evaluation;
                this.groupedMatrices = this.normalizeMatrices(
                    evaluation?.matrices ?? []
                );
                this.standaloneStandards = evaluation?.standards ?? [];
                this.standards = this.getFlattenedStandards(
                    this.groupedMatrices,
                    this.standaloneStandards
                );

                if (!this.standards.length) {
                    this.hasNoEvaluationDefinition = true;
                    this.createItem();
                    this.isLoadingDefinition = false;
                    return;
                }

                this.hasNoEvaluationDefinition = false;

                if (keepExistingRecords) {
                    this.hydrateItemRecordsWithStandards();
                } else {
                    this.createItem();
                }

                this.isLoadingDefinition = false;
            },
            error: () => {
                this.isLoadingDefinition = false;
                this.hasNoEvaluationDefinition = true;
                this.createItem();
            },
        });
    }

    private hydrateItemRecordsWithStandards(): void {
        this.item = {
            ...(this.item ?? {}),
            records: this.buildRecordsFromStandards(this.item?.records ?? []),
        };

        this.refreshRecordsMap();
        this.checkForEmptyValues();
    }

    private createItem(): void {
        this.item = {
            note: this.item?.note ?? '',
            records: this.buildRecordsFromStandards(),
        };

        this.refreshRecordsMap();
        this.checkForEmptyValues();
    }

    private refreshRecordsMap(): void {
        this.recordsByStandardId.clear();

        (this.item?.records ?? []).forEach(record => {
            if (record.standardId) {
                this.recordsByStandardId.set(record.standardId, record);
            }
        });
    }

    private buildRecordsFromStandards(
        existingRecords: EvaluationStandardRecord[] = []
    ): EvaluationStandardRecord[] {
        const byStandardId = new Map<string, EvaluationStandardRecord>();
        const byName = new Map<string, EvaluationStandardRecord>();

        existingRecords.forEach(record => {
            if (record.standardId) {
                byStandardId.set(record.standardId, record);
            }

            byName.set(record.name ?? '', record);
        });

        return (this.standards ?? []).map(standard => {
            const source =
                (standard.id ? byStandardId.get(standard.id) : undefined) ??
                byName.get(standard.name ?? '');

            return {
                id: source?.id,
                standardId: standard.id,
                name: standard.name ?? source?.name ?? '',
                from: standard.from ?? source?.from,
                target: standard.target ?? source?.target ?? 0,
                value: source?.value,
                note: source?.note ?? '',
                selectedRubricLevelId: source?.selectedRubricLevelId,
                selectedRubricLevelName: source?.selectedRubricLevelName,
                selectedRubricLevelColor: source?.selectedRubricLevelColor,
            };
        });
    }

    private mapRecordPayload(
        record: EvaluationStandardRecord,
        forCreate: boolean
    ): EvaluationStandardRecordValue {
        if (forCreate) {
            return {
                standardId: record.standardId,
                rubricLevelId: record.selectedRubricLevelId,
                value: record.value,
                note: record.note ?? '',
            };
        }

        if (record.id) {
            return {
                id: record.id,
                rubricLevelId: record.selectedRubricLevelId,
                value: record.value,
                note: record.note ?? '',
            };
        }

        return {
            standardId: record.standardId,
            rubricLevelId: record.selectedRubricLevelId,
            value: record.value,
            note: record.note ?? '',
        };
    }

    private normalizeMatrices(
        matrices: EvaluationMatrix[]
    ): EvaluationMatrix[] {
        return matrices.map(matrix => {
            const groupStandards = (matrix.groups ?? []).flatMap(
                group => group.standards ?? []
            );

            return {
                ...matrix,
                groups: [],
                standards: this.dedupeStandardsById([
                    ...(matrix.standards ?? []),
                    ...groupStandards,
                ]),
            };
        });
    }

    private getFlattenedStandards(
        matrices: EvaluationMatrix[],
        standalone: EvaluationStandard[]
    ): EvaluationStandard[] {
        const matrixStandards = matrices.flatMap(
            matrix => matrix.standards ?? []
        );

        return this.dedupeStandardsById([...matrixStandards, ...standalone]);
    }

    // Only ids are safe to compare: two criteria in different matrices may
    // legitimately share the same name and range.
    private dedupeStandardsById(
        standards: EvaluationStandard[]
    ): EvaluationStandard[] {
        const seen = new Set<string>();

        return standards.filter(standard => {
            if (!standard.id) return true;
            if (seen.has(standard.id)) return false;

            seen.add(standard.id);
            return true;
        });
    }

    private getDefaultLevelValue(level: EvaluationStandardRubricLevel): number {
        if (level.from !== undefined && level.from !== null) {
            if (level.to !== undefined && level.to !== null) {
                return Math.round(((level.from + level.to) / 2) * 100) / 100;
            }

            return level.from;
        }

        return level.to ?? 0;
    }
}
